import { onCLS } from 'web-vitals'
import { useNuxtApp, useRoute } from 'nuxt/app'
import useABTestV2 from '@/utils/abtest/useABTestV2'
import { CLS_MONITORING_AB_TEST_CONFIG } from '@/utils/abtest/abTestConfig'
import PageDetector from '@/utils/PageDetector'

/**
 * Monitors CLS and reports high values to Bugsnag
 * Only reports when CLS > 0.35
 * @returns {Function} Cleanup function to stop monitoring
 */
/**
 * Determines if CLS monitoring should be active based on A/B test pattern
 * For top page, only monitors users in B pattern (10%)
 * For other pages, always monitors CLS
 * @returns {boolean} Whether CLS monitoring should be active
 */
function shouldMonitorCLS() {
  const route = useRoute()
  const pageDetector = new PageDetector(route)
  const isTopPage = pageDetector.isTopPage
  if (!isTopPage) return true

  const { isBPattern } = useABTestV2(CLS_MONITORING_AB_TEST_CONFIG)
  return isBPattern
}

export function monitorCLS() {
  const shouldMonitor = shouldMonitorCLS()
  let clsMonitorActive = shouldMonitor
  let maxCLSValue = 0
  if (!clsMonitorActive) return () => {}
  console.log('CLS monitoring is active')

  onCLS(metric => {
    if (!clsMonitorActive || metric.value <= 0.35) return

    maxCLSValue = Math.max(maxCLSValue, metric.value)
    let networkInfo = 'unknown'
    if (navigator.connection) {
      networkInfo = navigator.connection.effectiveType || 'unknown'
    }

    const deviceInfo = {
      userAgent: navigator.userAgent,
      deviceType: getDeviceType(),
      viewport: `${window.innerWidth}x${window.innerHeight}`
    }

    if (metric.entries && metric.entries.length > 0) {
      const lastEntry = metric.entries[metric.entries.length - 1]

      if (lastEntry.sources && lastEntry.sources.length > 0) {
        const clsData = {
          cls: metric.value,
          networkInfo,
          deviceInfo,
          elements: []
        }

        // Get information about elements that shifted
        lastEntry.sources.forEach(source => {
          const element = source.node
          if (!element) return

          const elementTag = element.tagName.toLowerCase()
          const elementId = element.id ? `#${element.id}` : ''
          const elementClasses = Array.from(element.classList)
            .map(c => `.${c}`)
            .join('')
          const selector = `${elementTag}${elementId}${elementClasses}`

          const elementPath = getElementPath(element)
          clsData.elements.push({
            selector,
            path: elementPath
          })
        })

        reportToBugsnag(clsData)
      }
    }
  })

  // Return cleanup function
  return () => {
    clsMonitorActive = false
  }
}
/**
 * Gets the element path for easier identification
 * @param {HTMLElement} element - The DOM element
 * @returns {string} Element path
 */
function getElementPath(element, maxDepth = 3) {
  const path = []
  let currentElement = element
  let depth = 0

  while (currentElement && currentElement.tagName && depth < maxDepth) {
    let elementIdentifier = currentElement.tagName.toLowerCase()

    if (currentElement.id) {
      elementIdentifier += `#${currentElement.id}`
    } else if (currentElement.classList && currentElement.classList.length > 0) {
      elementIdentifier += `.${Array.from(currentElement.classList).join('.')}`
    }

    path.unshift(elementIdentifier)
    currentElement = currentElement.parentElement
    depth++
  }

  return path.join(' > ')
}
/**
 * @returns {string}
 */
function getDeviceType() {
  const ua = navigator.userAgent
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet'
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile'
  }
  return 'desktop'
}
/**
 * @param {Object} clsData
 */
function reportToBugsnag(clsData) {
  const errorMessage = `[CLS] High value detected:
    cls: ${clsData.cls.toFixed(4)}
    network: ${clsData.networkInfo}
    device: ${clsData.deviceInfo.deviceType}
    viewport: ${clsData.deviceInfo.viewport}
    elements: ${clsData.elements.map(e => e.path).join(', ')}`

  const nuxtApp = useNuxtApp()
  if (nuxtApp.$bugsnag) nuxtApp.$bugsnag.notify(errorMessage)
}
